import React, { useState, useEffect } from 'react';
import { CAREER_TYPES, OFFICIAL_EMAILS } from '../../constants';
import { volunteerApplicationsService, jobPostingsService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Spinner from '../../components/ui/Spinner';
import SEO from '../../components/common/SEO';
import {
  VolunteerIcon,
  BriefcaseIcon,
  GridCategoryIcon,
  SproutIcon,
  ClockIcon,
  LocationPinIcon,
  CheckCircleIcon,
  WarningIcon,
  BibleIcon,
  GlobeLanguageIcon,
  ShieldIcon,
  HandshakeIcon,
  CheckIcon,
} from '../../components/common/Icons';

const getRoleIconMeta = (pos) => {
  const iconStr = String(pos?.icon || '').trim();
  const title = String(pos?.title || '').toLowerCase();
  const cat = String(pos?.category || '').toLowerCase();

  // 1. Translation / Regional Language (replaces 🌐 emoji)
  if (iconStr === '🌐' || title.includes('translat') || title.includes('language') || title.includes('regional')) {
    return {
      icon: <GlobeLanguageIcon size={24} style={{ color: 'var(--clr-primary)' }} />,
      isSecondary: false,
    };
  }
  // 2. Bible / Scripture Content Writing & Reviewing (replaces 📖 emoji)
  if (iconStr === '📖' || title.includes('content') || title.includes('writer') || title.includes('scripture') || title.includes('review')) {
    return {
      icon: <BibleIcon size={24} style={{ color: '#C08552' }} />,
      isSecondary: true,
    };
  }
  // 3. Community & League Moderation (replaces 🛡️ emoji)
  if (iconStr === '🛡️' || iconStr === '🛡' || title.includes('moderator') || title.includes('community')) {
    return {
      icon: <ShieldIcon size={24} style={{ color: 'var(--clr-primary)' }} />,
      isSecondary: false,
    };
  }
  // 4. Outreach / Church League Partnerships (replaces 🤝 emoji)
  if (iconStr === '🤝' || title.includes('outreach') || title.includes('lead') || title.includes('church') || title.includes('league')) {
    return {
      icon: <HandshakeIcon size={24} style={{ color: '#C08552' }} />,
      isSecondary: true,
    };
  }
  // 5. Full Time Career
  if (pos?.type === CAREER_TYPES.fullTime || pos?.type === 'full_time' || title.includes('full-time') || title.includes('full time')) {
    return {
      icon: <BriefcaseIcon size={24} style={{ color: 'var(--clr-primary)' }} />,
      isSecondary: false,
    };
  }
  // 6. Youth Ministry
  if (iconStr === '🌱' || title.includes('youth') || cat.includes('youth')) {
    return {
      icon: <SproutIcon size={24} style={{ color: '#C08552' }} />,
      isSecondary: true,
    };
  }

  // Fallback for volunteer
  return {
    icon: <VolunteerIcon size={24} style={{ color: 'var(--clr-primary)' }} />,
    isSecondary: false,
  };
};

const VolunteerAndCareers = () => {
  const { user } = useAuth();

  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [filterType, setFilterType] = useState('all'); // 'all', 'volunteer', 'full_time'
  const [selectedRole, setSelectedRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  useEffect(() => {
    setLoadingPositions(true);
    // Real-time subscription to active job postings directly from Firestore for all visitors
    const unsub = jobPostingsService.subscribeActive((activeList) => {
      const onlyActive = (activeList || []).filter((p) => p.status === 'active');
      setPositions(onlyActive);
      setLoadingPositions(false);
    });

    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  // Form State
  const [formData, setFormData] = useState({
    fullName: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    location: '',
    roleId: '',
    roleTitle: '',
    roleType: '',
    weeklyHours: '5',
    experience: '',
    faithMotivation: '',
    portfolioUrl: '',
  });

  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: prev.fullName || user.displayName || '',
        email: prev.email || user.email || '',
      }));
    }
  }, [user]);

  const filteredPositions = positions.filter((pos) => {
    if (filterType === 'all') return true;
    if (filterType === CAREER_TYPES.fullTime || filterType === 'full_time') {
      return pos.type === 'full_time' || pos.type === CAREER_TYPES.fullTime;
    }
    return pos.type === filterType;
  });

  const handleOpenApply = (pos) => {
    setSelectedRole(pos);
    setFormData((prev) => ({
      ...prev,
      roleId: pos.id,
      roleTitle: pos.title,
      roleType: pos.type,
      weeklyHours: pos.type === CAREER_TYPES.fullTime ? '40' : '5',
    }));
    setSubmitSuccess(null);
    setFormError('');
    setIsModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.fullName.trim() || !formData.email.trim() || !formData.faithMotivation.trim()) {
      setFormError('Please fill out all required fields marked with * (Full Name, Email, and Faith Motivation).');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await volunteerApplicationsService.submit({
        ...formData,
        userId: user?.uid || '',
      });
      setSubmitSuccess(result);
    } catch (err) {
      console.error('Failed to submit application:', err);
      const errMsg = err?.message?.includes('permission') 
        ? 'Database write permission issue. Please make sure Firebase Firestore security rules allow public application submissions.'
        : 'Failed to submit application. Please try again or reach out to support@biblecontest.app directly.';
      setFormError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="careers-page">
      <SEO
        title="Volunteer & Ministry Careers — Serve with Bible Contest"
        description="Use your God-given gifts to build Scripture study apps, write & verify quiz questions, translate content, and mentor youth across global church leagues."
        keywords="Christian ministry careers, Christian volunteer opportunities, Bible app development, Christian non profit jobs, volunteer Bible quiz writer"
        canonicalUrl="/volunteer"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Volunteer & Ministry Careers — Bible Contest",
          "url": "https://biblecontest.app/volunteer",
          "description": "Serve in global Scripture education. Apply for volunteer and ministry roles with Bible Contest."
        }}
      />

      {/* Hero Header */}
      <section className="careers-hero">
        <span className="hero-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <SproutIcon size={14} style={{ color: 'var(--clr-primary)' }} /> SERVE WITH PURPOSE &amp; IMPACT
        </span>
        <h1 className="careers-hero__title">
          Use Your God-Given Talents to Draw Hearts Closer to Scripture
        </h1>
        <p className="careers-hero__subtitle">
          Whether you can dedicate 3 hours a week as a volunteer Bible question writer or join full-time as an engineer or ministry leader, your gifts will inspire thousands of youth and believers to cherish God's Word every day.
        </p>

        {/* iOS-Style Segmented Filter Control */}
        <div className="volunteer-segmented-control-wrapper">
          <div className="volunteer-segmented-control" role="tablist" aria-label="Filter opportunities by commitment">
            <button
              type="button"
              role="tab"
              aria-selected={filterType === 'all'}
              className={`volunteer-segment-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              <span className="segment-icon"><GridCategoryIcon size={15} /></span>
              <span className="segment-label">All Roles</span>
              <span className="segment-count">{positions.length}</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={filterType === CAREER_TYPES.volunteer}
              className={`volunteer-segment-btn ${filterType === CAREER_TYPES.volunteer ? 'active' : ''}`}
              onClick={() => setFilterType(CAREER_TYPES.volunteer)}
            >
              <span className="segment-icon"><VolunteerIcon size={16} /></span>
              <span className="segment-label">Volunteer</span>
              <span className="segment-count">
                {positions.filter((p) => p.type === CAREER_TYPES.volunteer).length}
              </span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={filterType === CAREER_TYPES.fullTime}
              className={`volunteer-segment-btn ${filterType === CAREER_TYPES.fullTime ? 'active' : ''}`}
              onClick={() => setFilterType(CAREER_TYPES.fullTime)}
            >
              <span className="segment-icon"><BriefcaseIcon size={15} /></span>
              <span className="segment-label">Full-Time</span>
              <span className="segment-count">
                {positions.filter((p) => p.type === CAREER_TYPES.fullTime || p.type === 'full_time').length}
              </span>
            </button>
          </div>

          {/* Contextual subtext under the filter */}
          <div className="volunteer-filter-summary">
            {filterType === 'all' && (
              <span>Showing all <strong>{positions.length}</strong> opportunities · Remote &amp; Flexible</span>
            )}
            {filterType === CAREER_TYPES.volunteer && (
              <span>Showing <strong>{positions.filter((p) => p.type === CAREER_TYPES.volunteer).length}</strong> volunteer ministry roles · 2–5 hrs/week</span>
            )}
            {filterType === CAREER_TYPES.fullTime && (
              <span>Showing <strong>{positions.filter((p) => p.type === CAREER_TYPES.fullTime || p.type === 'full_time').length}</strong> core leadership &amp; ministry role</span>
            )}
          </div>
        </div>
      </section>

      {/* Positions Grid */}
      <section className="careers-grid">
        {loadingPositions ? (
          <div style={{ gridColumn: '1 / -1', padding: '60px 20px', textAlign: 'center', color: 'var(--clr-muted)' }}>
            <Spinner />
            <div style={{ marginTop: '12px', fontSize: 'var(--text-sm)' }}>Loading available opportunities...</div>
          </div>
        ) : filteredPositions.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', padding: '60px 20px', textAlign: 'center', color: 'var(--clr-muted)', background: 'var(--clr-surface)', borderRadius: '16px', border: '1px solid var(--clr-border)' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'var(--clr-primary-subtle)', color: 'var(--clr-primary)', marginBottom: '12px', border: '1px solid var(--clr-primary-light)' }}>
              <SproutIcon size={28} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--clr-text-1)', marginBottom: '6px' }}>No Active Postings in this Category</h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--clr-text-2)', maxWidth: '460px', margin: '0 auto' }}>
              There are currently no listed openings under this filter. We are always eager to meet passionate believers—send a general inquiry below!
            </p>
          </div>
        ) : (
          filteredPositions.map((pos) => {
            const isVolunteer = pos.type === CAREER_TYPES.volunteer;
            const iconMeta = getRoleIconMeta(pos);
            return (
              <div key={pos.id} className="career-card">
                <div className="career-card__header">
                  <div className={`career-card__icon ${iconMeta.isSecondary ? 'career-card__icon--secondary' : ''}`}>
                    {iconMeta.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="career-card__title">{pos.title}</h3>
                    <div className="career-card__meta">
                      <Badge
                        label={isVolunteer ? 'Volunteer Service' : 'Full Time'}
                        variant={isVolunteer ? 'info' : 'primary'}
                      />
                      <span className="meta-text" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <LocationPinIcon size={13} style={{ color: 'var(--clr-muted)' }} /> {pos.location}
                      </span>
                      <span className="meta-text" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <ClockIcon size={13} style={{ color: 'var(--clr-muted)' }} /> {pos.commitment}
                      </span>
                    </div>
                  </div>
                </div>


                <p className="career-card__desc">{pos.shortDescription}</p>

                <div className="career-card__section">
                  <strong>Key Ways You'll Serve:</strong>
                  <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '6px 0 0' }}>
                    {(Array.isArray(pos.responsibilities) ? pos.responsibilities : [pos.responsibilities].filter(Boolean)).map((r, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                        <CheckIcon size={13} style={{ color: 'var(--clr-primary)', flexShrink: 0, marginTop: '3px' }} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="career-card__section">
                  <strong>What We Look For:</strong>
                  <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '6px 0 0' }}>
                    {(Array.isArray(pos.requirements) ? pos.requirements : [pos.requirements].filter(Boolean)).map((req, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                        <CheckIcon size={13} style={{ color: '#C08552', flexShrink: 0, marginTop: '3px' }} />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="career-card__footer">
                  <Button
                    variant={isVolunteer ? 'secondary' : 'primary'}
                    size="md"
                    onClick={() => handleOpenApply(pos)}
                    style={{ width: '100%', minHeight: '44px' }}
                  >
                    Apply to Serve in this Role →
                  </Button>
                </div>
              </div>
            );
          }))}
      </section>

      {/* General Volunteering Banner */}
      <section className="general-volunteer-banner card">
        <div className="banner-content">
          <h3>Have a unique gift or talent to offer?</h3>
          <p>
            We welcome prayer warriors, audio voiceover artists, youth pastors, social media creators, translators, and software engineers. Let us know how you would love to serve!
          </p>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
            <Button
              variant="ghost"
              onClick={() => handleOpenApply({
                id: 'general-inquiry',
                title: 'General Ministry & Volunteer Inquiry',
                type: CAREER_TYPES.volunteer,
                commitment: 'Flexible',
                location: 'Remote',
              })}
            >
              Submit General Volunteer Inquiry
            </Button>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--clr-muted)', marginTop: '14px', marginBottom: 0 }}>
            Prefer direct email? Reach our team anytime at{' '}
            <a href={`mailto:${OFFICIAL_EMAILS.manager}`} style={{ color: 'var(--clr-primary)', fontWeight: 600 }}>
              {OFFICIAL_EMAILS.manager}
            </a>
            {' '}or{' '}
            <a href={`mailto:${OFFICIAL_EMAILS.support}`} style={{ color: 'var(--clr-primary)', fontWeight: 600 }}>
              {OFFICIAL_EMAILS.support}
            </a>
          </p>
        </div>
      </section>

      {/* Application Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={submitSuccess ? 'Application Received!' : `Apply: ${selectedRole?.title || 'Volunteer Role'}`}
          size="lg"
        >
          {submitSuccess ? (
            <div className="modal__body">
              <div className="application-success">
                <div className="success-icon" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: 'var(--clr-primary-subtle)', color: 'var(--clr-primary)', margin: '0 auto 16px', border: '1px solid var(--clr-primary-light)' }}>
                  <CheckCircleIcon size={36} />
                </div>
                <h3>Thank You, {formData.fullName}!</h3>
                <p>
                  Your application for <strong>{formData.roleTitle}</strong> has been received by our ministry and admin team.
                </p>
                <div className="success-ref-box">
                  <span>Application Reference ID:</span>
                  <code>{submitSuccess.id}</code>
                </div>
                <p className="success-note">
                  We review every application prayerfully and will reach out to you at <strong>{formData.email}</strong>.
                </p>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSubmitSuccess(null);
                  }}
                  style={{ marginTop: '20px' }}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitApplication} className="application-form">
              <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Role Summary Pill */}
                {selectedRole && (
                  <div
                    style={{
                      background: 'var(--clr-surface-container)',
                      border: '1px solid var(--clr-border)',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '8px',
                      fontSize: '13px',
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--clr-muted)', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, display: 'block' }}>Applying For:</span>
                      <strong style={{ color: 'var(--clr-text-1)' }}>{selectedRole.title}</strong>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <Badge
                        label={selectedRole.type === CAREER_TYPES.volunteer ? 'Volunteer' : 'Full Time'}
                        variant={selectedRole.type === CAREER_TYPES.volunteer ? 'info' : 'primary'}
                      />
                      <span style={{ fontSize: '12px', color: 'var(--clr-muted)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <LocationPinIcon size={13} /> {selectedRole.location || 'Remote'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Form Error Banner */}
                {formError && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: 'var(--radius-md)',
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#f87171',
                    fontSize: '13px',
                    lineHeight: '1.4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <WarningIcon size={16} style={{ flexShrink: 0 }} />
                    <span>{formError}</span>
                  </div>
                )}


                {/* Form Fields */}
                <div className="form-grid-2">
                  <Input
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleFormChange}
                    placeholder="e.g. John Doe"
                    required
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="name@example.com"
                    required
                  />
                </div>

                <div className="form-grid-2">
                  <Input
                    label="Phone / WhatsApp"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="+1 (555) 000-0000"
                  />
                  <Input
                    label="City, State & Country"
                    name="location"
                    value={formData.location}
                    onChange={handleFormChange}
                    placeholder="e.g. Dallas, TX or Hyderabad, India"
                  />
                </div>

                <div className="form-grid-2">
                  <Input
                    label="Portfolio / LinkedIn / Resume Link"
                    type="url"
                    name="portfolioUrl"
                    value={formData.portfolioUrl}
                    onChange={handleFormChange}
                    placeholder="https://linkedin.com/in/... or Google Drive link"
                  />
                  <Input
                    label={formData.roleType === CAREER_TYPES.fullTime ? 'Weekly Availability (hrs/week)' : 'Weekly Volunteer Time (hrs/week)'}
                    type="number"
                    name="weeklyHours"
                    value={formData.weeklyHours}
                    onChange={handleFormChange}
                    min="1"
                    max="60"
                  />
                </div>

                <Textarea
                  label="Relevant Skills & Experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleFormChange}
                  placeholder="Briefly describe your background, tech stack, ministry involvement, or translation languages..."
                  rows={2}
                />

                <Textarea
                  label="Why do you want to serve with Bible Contest?"
                  name="faithMotivation"
                  value={formData.faithMotivation}
                  onChange={handleFormChange}
                  placeholder="Tell us about your faith journey, passion for Scripture, and why you'd love to serve..."
                  rows={3}
                  required
                />
              </div>

              <div className="modal__footer">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
};

export default VolunteerAndCareers;
